import type { NotificationModel } from "@/features/notifications/api/models/NotificationModel";
import type { NotificationUnreadCountModel } from "@/features/notifications/api/models/NotificationUnreadCount";
import { useHTTP, useHTTPQuery, type PaginatedResponseModel } from "@/lib/http";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useEffect } from "react";

export const useNotification = (notificationId: string) => {
	const http = useHTTP("Required");
	const queryClient = useQueryClient();

	const result = useHTTPQuery({
		queryKey: ["notification", notificationId],
		queryFn: () => {
			return http<NotificationModel>({
				method: "GET",
				url: `/notification/${notificationId}`
			});
		}
	});

	useEffect(() => {
		if (!result.isSuccess) {
			return;
		}

		const notificationsInfiniteData = queryClient.getQueryData<InfiniteData<PaginatedResponseModel<NotificationModel>>>(["notifications"]);
		if (!notificationsInfiniteData) {
			return;
		}

		const matchedNotification = notificationsInfiniteData.pages
			.flatMap(page => page.results)
			.find(notification => notification.notificationId === notificationId);

		if (!matchedNotification || matchedNotification.readAt) {
			return;
		}

		queryClient.setQueryData<InfiniteData<PaginatedResponseModel<NotificationModel>>>(
			["notifications"],
			oldData => {
				if (!oldData) {
					return oldData;
				}

				return {
					...oldData,
					pages: oldData.pages.map(page => ({
						...page,
						results: page.results.map(notification =>
							notification.notificationId === notificationId
								? {
									...notification,
									readAt: new Date().toISOString()
								}
								: notification
						)
					}))
				};
			}
		);

		const notificationsUnreadCount = queryClient.getQueryData<NotificationUnreadCountModel>(["notificationUnreadCount"]);
		if (!notificationsUnreadCount) {
			return;
		}

		queryClient.setQueryData<NotificationUnreadCountModel>(
			["notificationUnreadCount"],
			(oldData) => {
				if (!oldData) {
					return oldData;
				}

				return {
					...oldData,
					unreadCount: notificationsUnreadCount.unreadCount > 0 ? notificationsUnreadCount.unreadCount - 1 : 0
				};
			}
		);
	}, [result.isSuccess]);

	return result;
};
